import { useEffect, useRef } from 'react';
import { setNewLayout } from '../../actions/subscriber';
import { handleToggleSidebar } from '../../utils';

/**
 * Closes the sidebar sub menu when the tour ends.
 *
 * When showTour turns false, React re-renders the className of .c-wrapper (show-tour) and of the
 * sidebar (tour-element). That wipes the show-nav-child / minimize-nav classes handleToggleSidebar
 * added to the DOM, so the content loses its left offset. Exits that only set showTour: false
 * (e.g. GO TO ROADMAP on the last Form Categories step) never re-run the sidebar sync, and the sub
 * menu stayed open on top of the page content until the user toggled the menu.
 *
 * Only acts when the tour ends while forceShowNavChild (set by the tour steps) is still true, i.e. the
 * exit did not clean up the layout itself. Exits that reset it together with showTour (closeTour,
 * skipTour, wizard skips) keep their current behaviour. When the tour is shown again, TheTour calls
 * handleShowTour(), which re-applies the layout of the current step.
 */
export const useCloseNavChildAfterTour = ({ showTour, forceShowNavChild, activeMainNavItem, forceMinimizeNav, dispatch }) => {
     const prevShowTourRef = useRef(showTour);
     const pendingCloseRef = useRef(false);

     useEffect(() => {
          const tourJustEnded = prevShowTourRef.current && !showTour;
          prevShowTourRef.current = showTour;

          if (showTour) {
               pendingCloseRef.current = false;
               return;
          }

          if (tourJustEnded && forceShowNavChild) {
               // Let the sidebar className re-render without show-nav-child first, then fix the DOM on the next run.
               pendingCloseRef.current = true;
               dispatch(setNewLayout({ forceShowNavChild: false }));
               return;
          }

          if (pendingCloseRef.current && !forceShowNavChild) {
               pendingCloseRef.current = false;
               handleToggleSidebar({
                    activeMainNavItem,
                    toggleShowNavChild: true,
                    forceShowNavChild: false,
                    toggleMinimizeNav: true,
                    forceMinimizeNav: !!forceMinimizeNav || window.innerWidth < 1430,
               });
          }
     }, [showTour, forceShowNavChild]); // eslint-disable-line react-hooks/exhaustive-deps
};
